// C:\VUE3\platform\src\store\couponStore.js
// ✅ CouponSection 컴포넌트 지원을 위한 업데이트 (Options API 스타일 유지)

import { defineStore } from "pinia";
import mallApi from "@/common/api/mallApi";
import { logDebug, logError } from "@/utils/logHelper";

// 쿠폰 상태 상수
const COUPON_STATUS = Object.freeze({
  AVAILABLE: "A",
  USED: "U",
  EXPIRED: "E",
  CANCELLED: "C",
});

export const useCouponStore = defineStore("coupon", {
  /* =========================================================
    State
  ========================================================= */
  state: () => ({
    /** 전체 쿠폰 목록 */
    coupons: [],
    
    /** 현재 선택된 쿠폰 */
    selectedCoupon: null,
    
    /** 로딩 상태 */
    isLoading: false,
    
    /** 마지막 조회한 userSuid (중복 호출 방지) */
    lastFetchedUser: null,
  }),

  /* =========================================================
    Getters
  ========================================================= */
  getters: {
    /** 사용 가능한 쿠폰만 필터링 */
    availableCoupons(state) {
      return state.coupons.filter(
        (c) => c.couponUseStatus === COUPON_STATUS.AVAILABLE
      );
    },

    /** 사용 가능한 쿠폰 개수 */
    availableCount(state) {
      return this.availableCoupons.length;
    },

    /** 선택된 쿠폰 존재 여부 */
    hasSelectedCoupon(state) {
      return state.selectedCoupon !== null;
    },
  },

  /* =========================================================
    Actions
  ========================================================= */
  actions: {
    /**
     * 선택된 쿠폰 설정
     * @param {Object|null} coupon - 쿠폰 객체 또는 null
     */
    setSelectedCoupon(coupon) {
      this.selectedCoupon = coupon;
      logDebug("[couponStore] setSelectedCoupon:", coupon?.userCouponCode || null);
    },

    /**
     * 선택된 쿠폰 해제
     */
    clearSelectedCoupon() {
      this.selectedCoupon = null;
      logDebug("[couponStore] Coupon selection cleared");
    },

    /**
     * 사용 가능한 쿠폰 목록 조회
     * @param {string} userSuid - 사용자 식별자
     * @param {boolean} force - 강제 재조회 여부
     * @returns {Promise<Array>} 사용 가능한 쿠폰 목록
     */
    async fetchAvailableCoupons(userSuid, force = false) {
      if (!userSuid) {
        logDebug("[couponStore] No userSuid provided");
        return [];
      }

      // 중복 호출 방지 (같은 유저, force가 아닌 경우)
      if (!force && this.lastFetchedUser === userSuid && this.coupons.length > 0) {
        logDebug("[couponStore] Using cached coupons");
        return this.availableCoupons;
      }

      this.isLoading = true;

      try {
        const response = await mallApi.getCouponById({ userSuid });

        if (response?.userCouponsList && Array.isArray(response.userCouponsList)) {
          this.coupons = response.userCouponsList;
          this.lastFetchedUser = userSuid;

          logDebug("[couponStore] Fetched coupons:", {
            total: this.coupons.length,
            available: this.availableCoupons.length,
          });
        } else {
          this.coupons = [];
        }

        return this.availableCoupons;
      } catch (error) {
        logError("[couponStore] Failed to fetch coupons:", error);
        this.coupons = [];
        return [];
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 쿠폰 코드로 쿠폰 등록
     * @param {string} userSuid - 사용자 식별자
     * @param {string} couponCode - 쿠폰 코드
     * @returns {Promise<Object|null>} 등록된 쿠폰 또는 null
     */
    async registerCouponByCode(userSuid, couponCode) {
      if (!userSuid || !couponCode) {
        logDebug("[couponStore] Missing userSuid or couponCode");
        return null;
      }

      this.isLoading = true;

      try {
        const response = await mallApi.registerCoupon?.({
          userSuid,
          couponCode: couponCode.trim(),
        });

        if (response?.success) {
          // 쿠폰 목록 갱신
          await this.fetchAvailableCoupons(userSuid, true);
          logDebug("[couponStore] Coupon registered:", couponCode);
          return response.coupon || response;
        }

        return null;
      } catch (error) {
        logError("[couponStore] Failed to register coupon:", error);
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 스토어 초기화 (로그아웃 시 등)
     */
    reset() {
      this.coupons = [];
      this.selectedCoupon = null;
      this.lastFetchedUser = null;
      this.isLoading = false;
      logDebug("[couponStore] Store reset");
    },
  },
});